import React, { useRef, useState, useEffect } from 'react';
import { UploadCloud, X, FileJson, ClipboardPaste, CheckCircle2, AlertCircle } from 'lucide-react';
import { CircularProgress } from '@mui/material';
import LottieLoad from '../LotteiLoad';

/**
 * Reusable media uploader with drag & drop, click-to-browse, paste (Ctrl/Cmd + V),
 * and a live preview (image / video / Lottie-JSON).
 *
 * Controlled: pass `file` (File | null) and `onFile(file | null)`.
 * Validation (type / size) is left to the PARENT — it runs inside the onFile callback.
 *
 * Paste is scoped to this focusable element so a stray Ctrl+V elsewhere never hijacks
 * the page — click/focus the zone first, then paste.
 *
 * Props:
 *  - file        File | null        currently selected File
 *  - onFile      (File|null)=>void  drag/click/paste hand a File; the remove (x) sends null
 *  - accept      string             input accept attribute
 *  - kind        'image' | 'video'  drives preview rendering ('image' also handles .json → Lottie)
 *  - previewUrl  string | null      existing server media URL to preview (edit mode) when no File picked
 *  - processing  boolean            show a spinner overlay while the parent validates
 *  - status      'success'|'error'|null
 *  - disabled    boolean
 */
const isJsonFile = (f) => !!f && (f.type === 'application/json' || /\.json$/i.test(f.name || ''));

const formatSize = (bytes) => {
    if (bytes === undefined || bytes === null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const MediaDropzone = ({
    file,
    onFile,
    accept,
    kind = 'image',
    previewUrl = null,
    processing = false,
    status = null,
    disabled = false,
}) => {
    const [dragOver, setDragOver] = useState(false);
    const [objectUrl, setObjectUrl] = useState(null);
    const inputRef = useRef(null);

    const hasFile = file instanceof File;
    const fileIsJson = isJsonFile(file);

    // Create/revoke an object URL for the selected (non-json) File preview
    useEffect(() => {
        if (hasFile && !fileIsJson) {
            const url = URL.createObjectURL(file);
            setObjectUrl(url);
            return () => URL.revokeObjectURL(url);
        }
        setObjectUrl(null);
        return undefined;
    }, [file, hasFile, fileIsJson]);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (disabled) return;
        const f = e.dataTransfer?.files?.[0];
        if (f) onFile(f);
    };

    const handlePaste = (e) => {
        if (disabled) return;
        const pasted = e.clipboardData?.files?.[0];
        if (pasted) {
            e.preventDefault();
            onFile(pasted);
        }
    };

    const renderPreview = () => {
        // 1) A freshly-picked File
        if (hasFile) {
            if (fileIsJson) {
                return (
                    <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-xl bg-main-th text-main-blue flex items-center justify-center flex-shrink-0">
                            <FileJson size={26} />
                        </div>
                        <div className="text-left min-w-0">
                            <p className="text-sm font-medium text-main-font truncate max-w-[180px]">{file.name}</p>
                            <p className="text-xs text-main-subfont">Lottie / JSON · {formatSize(file.size)}</p>
                        </div>
                    </div>
                );
            }
            if (kind === 'video') {
                return <video src={objectUrl} className="max-h-44 rounded-xl" controls />;
            }
            return <img src={objectUrl} alt={file.name} className="max-h-44 rounded-xl object-contain" />;
        }
        // 2) Existing server media (edit mode, nothing newly picked)
        if (previewUrl) {
            if (kind === 'video') {
                return <video src={previewUrl} className="max-h-44 rounded-xl" controls />;
            }
            if (/\.json($|\?)/i.test(previewUrl)) {
                return <div className="w-40 h-40"><LottieLoad url={previewUrl} /></div>;
            }
            return (
                <img
                    src={previewUrl}
                    alt="current"
                    className="max-h-44 rounded-xl object-contain"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
            );
        }
        return null;
    };

    const preview = renderPreview();

    return (
        <div
            tabIndex={disabled ? -1 : 0}
            onPaste={handlePaste}
            onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => !disabled && inputRef.current?.click()}
            className={`relative flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-4 py-6 text-center outline-none transition-all min-h-[150px]
                ${dragOver
                    ? 'border-main-blue bg-main-th'
                    : status === 'error'
                        ? 'border-main-red/60 bg-main-red/5'
                        : status === 'success'
                            ? 'border-main-blue/60 bg-main-th/40'
                            : 'border-main-border bg-main-bg hover:border-main-blue/50'}
                focus:border-main-blue focus:ring-4 focus:ring-main-blue/10
                ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
        >
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                className="hidden"
                disabled={disabled}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }}
            />

            {processing ? (
                <div className="flex flex-col items-center gap-2 text-main-subfont">
                    <CircularProgress size={28} sx={{ color: '#34C759' }} />
                    <span className="text-xs">Processing…</span>
                </div>
            ) : preview ? (
                <>
                    {preview}
                    <div className="flex items-center gap-1.5 text-xs text-main-subfont">
                        {status === 'success' && <CheckCircle2 size={14} className="text-main-blue" />}
                        {status === 'error' && <AlertCircle size={14} className="text-main-red" />}
                        <span>{dragOver ? 'Drop to replace' : 'Click, drop, or paste to replace'}</span>
                    </div>
                    {hasFile && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onFile(null); }}
                            className="absolute top-2 right-2 bg-main-delete text-white rounded-full p-1 shadow-md hover:bg-main-delete/90"
                            title="Remove file"
                        >
                            <X size={14} />
                        </button>
                    )}
                </>
            ) : (
                <>
                    <div className="w-12 h-12 rounded-xl bg-main-th text-main-blue flex items-center justify-center">
                        <UploadCloud size={22} />
                    </div>
                    <div className="text-xs text-main-subfont leading-relaxed">
                        <span className="font-medium text-main-font">Drag &amp; drop</span>, click to browse, or{' '}
                        <span className="inline-flex items-center gap-1 align-middle">
                            <ClipboardPaste size={12} /> paste
                        </span>
                        <div className="mt-1 text-[11px] text-main-subfont/80">
                            {kind === 'video' ? 'MP4 video (max 10 MB)' : 'Image PNG/JPG/GIF or Lottie JSON (max 1 MB)'}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default MediaDropzone;
